import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import firebase from 'firebase/compat/app';
import { useLoading } from '../../../../../../hooks/useLoading';
import { extractDomain } from '../../../../../../utils/validation/validationUtils';
import {
  loadBrandSetupData,
  determineStartingStep,
  transformToFormData
} from '../../../../../../utils/firebase/brandSetup';
import {
  loadPaymentSetupData,
  usesExternalGivingPage,
} from '../../../../../../utils/firebase/paymentSetup';

interface UseBrandSetupDataProps {
  charityId: string | undefined;
  methods?: UseFormReturn<any>;
}

interface UseBrandSetupDataReturn {
  loading: boolean;
  startingStep: number;
  error: string | null;
  setupMethod: string;
}

const MINIMUM_LOADING_DURATION_MS = 800;

export function useBrandSetupData({
  charityId,
  methods,
}: UseBrandSetupDataProps): UseBrandSetupDataReturn {
  const { loading, runWithLoading, setLoading } = useLoading(true);
  const [startingStep, setStartingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [setupMethod, setSetupMethod] = useState("fetch");

  useEffect(() => {
    if (!charityId) {
      setLoading(false);
      return;
    }

    runWithLoading(async () => {
      try {
        setError(null);
        const database = firebase.database();
        const [data, paymentData, charitySetupSnapshot, headerUrlSnapshot] = await Promise.all([
          loadBrandSetupData(charityId),
          loadPaymentSetupData(charityId),
          database.ref(`${charityId}/charitySetup`).once('value'),
          database.ref(`${charityId}/config/headerRedirectUrl`).once('value'),
        ]);
        const charitySetup = charitySetupSnapshot.val();
        const headerRedirectUrl = headerUrlSnapshot.val() as string | null;
        const externalGiving = usesExternalGivingPage(paymentData);
        setSetupMethod(data?.setupMethod || "fetch");
        setStartingStep(determineStartingStep(data, externalGiving));

        if (methods) {
          const formData = transformToFormData(data);
          if (!formData.brandUrl) {
            formData.brandUrl =
              data?.brandUrl ||
              charitySetup?.brandSetup?.brandUrl ||
              (headerRedirectUrl ? extractDomain(headerRedirectUrl) : "");
          }
          methods.reset(formData);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load brand setup data';
        console.error('Failed to load brand setup data:', err);
        setError(errorMessage);
      }
    }, MINIMUM_LOADING_DURATION_MS);
  }, [charityId, runWithLoading, setLoading, methods]);

  return {
    loading,
    startingStep,
    error,
    setupMethod,
  };
}
